import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map } from 'rxjs';

import { AuthService } from '@auth/services/auth.service';
import { PermissionService } from '@auth/services/permission.service';

/**
 * Guard de ruta basado en recursos. Permite el acceso si el usuario tiene el
 * recurso indicado (o cualquiera de la lista); si no, redirige a /home.
 *
 * Los claims se cachean en localStorage con un fetch asincrono posterior al
 * login (AuthService.fetchAndCacheClaims), asi que una carga de pagina
 * completa sobre una ruta guardada puede evaluar este guard con los claims
 * aun "frios" (claims() === null) para un usuario autenticado y autorizado
 * (p. ej. un deep-link inmediatamente despues del login). En ese caso el
 * guard resuelve los claims contra el API antes de decidir, en vez de
 * expulsar al usuario a /home por una carrera de arranque.
 */
export const recursoGuard = (recurso: string | readonly string[]): CanActivateFn => {
  return () => {
    const auth = inject(AuthService);
    const permission = inject(PermissionService);
    const router = inject(Router);

    const recursos = Array.isArray(recurso) ? recurso : [recurso];

    if (permission.hasAnyPermission(recursos)) {
      return true;
    }

    if (auth.isAuthenticated() && auth.claims() === null) {
      return auth.loadClaims().pipe(
        map(() => {
          if (permission.hasAnyPermission(recursos)) {
            return true;
          }
          router.navigate(['/home']);
          return false;
        }),
      );
    }

    router.navigate(['/home']);
    return false;
  };
};
